import camelcaseKeys from 'camelcase-keys';
import z from 'zod';

import type { ApiOptions, ApiResponse } from '@api/ApiProvider';
import Api from '@api/ApiProvider';

const CertikHighlightSchema = z.object({
  data: z.number().optional(),
  label: z.string(),
});

const CertikAlertSchema = z.object({
  label: z.string(),
});

const SecurityScoreSchema = z.object({
  rank: z.number(),
  tier: z.string(),
  score: z.number(),
  codeSecurity: z.number(),
  communityTrust: z.number(),
  rankPercentile: z.number(),
  marketStability: z.number(),
  fundamentalHealth: z.number(),
  governanceStrength: z.number(),
  operationalResilience: z.number(),
});

const CertikDataSchema = z.object({
  alerts: z.array(CertikAlertSchema),
  cmcSlug: z.string(),
  updatedAt: z.string(),
  highlights: z.array(CertikHighlightSchema),
  tokenTickers: z.array(z.string()),
  coinGeckoSlug: z.string(),
  lastAuditDate: z.string(),
  securityScore: SecurityScoreSchema,
  projectWebsite: z.string(),
  auditedByCertiK: z.boolean(),
  certiKProjectId: z.string(),
  certiKProjectPage: z.string(),
  lastAuditDateEpoch: z.string(),
  projectTokenStatus: z.string(),
  primaryTokenContractAddress: z.string(),
});

export type CertikDataSchemaPayload = z.infer<typeof CertikDataSchema>;

const MetaSchema = z.object({
  marketCap: z.number(),
  volume24h: z.number(),
  circulatingSupply: z.number(),
  maxSupply: z.number(),
  FDMC: z.number(),
  percentChange24h: z.number(),
  certikData: CertikDataSchema.optional(),
});

export type MetaSchemaPayload = z.infer<typeof MetaSchema>;

const CoinInfoPayloadSchema = z
  .object({
    id: z.number(),
    cmc_token_id: z.number(),
    cg_token_id: z.string(),
    slug: z.string(),
    symbol: z.string(),
    name: z.string(),
    icon: z.string().url(),
    chain: z.string(),
    price: z.number(),
    meta: MetaSchema,
  })
  .transform((value) => camelcaseKeys(value));

export type CoinInfoPayload = z.infer<typeof CoinInfoPayloadSchema>;

export const getCoinInfo = async (
  idOrSlug: string | number,
  options?: ApiOptions<{ searchParams?: { withCertik?: boolean } }>,
): Promise<CoinInfoPayload> => {
  const { payload } = await Api.get(`v2/quote/coins/${idOrSlug}`, options).json<
    ApiResponse<CoinInfoPayload>
  >();

  return CoinInfoPayloadSchema.parse(payload);
};
